import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

const tabStyles = {
  width: 68,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: '2px',
  padding: '4px',
  borderRadius: '8px',
  cursor: 'pointer',
  textDecoration: 'none',
  userSelect: 'none' as const,
};

const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  backgroundColor: active ? '#e7ecfa' : 'transparent',
  color: active ? '#063FFE' : '#7e828f',
  flexShrink: 0,

  '& svg': {
    width: 24,
    height: 24,
  },
}));

const Label = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  fontSize: 10,
  fontWeight: active ? 500 : 400,
  lineHeight: '0.875rem',
  color: active ? '#063FFE' : '#7e828f',
  textAlign: 'center',
}));

const TabNavLink = styled(NavLink)(() => ({
  ...tabStyles,
  '&:hover .tab-icon-container': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const TabButton = styled(Box)(() => ({
  ...tabStyles,
  '&:hover .tab-icon-container': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

interface Props {
  readonly icon: ReactNode;
  readonly label: string;
  readonly active: boolean;
  readonly to?: string;
  readonly onClick?: () => void;
  readonly tag?: ReactNode;
}

export function SidebarTab({ icon, label, active, to, onClick, tag }: Props) {
  const content = (
    <>
      <IconContainer className="tab-icon-container" active={active}>
        {icon}
      </IconContainer>
      <Label active={active}>{label}</Label>
      {tag}
    </>
  );

  if (to) {
    return (
      <TabNavLink to={to} onClick={onClick}>
        {content}
      </TabNavLink>
    );
  }

  return <TabButton onClick={onClick}>{content}</TabButton>;
}
